import { useContext } from 'react';
import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import { useIntl, defineMessages } from 'react-intl';
import * as immutable from 'object-path-immutable';
import { get } from 'lodash';

import TASK_LIST_SECTION_DELETE from 'components/projects/taskList/gql/taskListSectionDelete.graphql';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import TemplateContext from 'components/projects/taskList/templates/TemplateContext';
import { TaskListSectionsFragment } from 'components/projects/taskList/gql/fragments';
import GET_TASK_LIST_HEADER_INFO from 'components/projects/taskList/gql/getTaskListHeaderInfo.graphql';
import toast from 'helpers/toast';

defineMessages({
  sectionDeleteError: {
    id: 'Projects.taskList.taskListSection.sectionDeleteError',
    defaultMessage: 'An error occurred updating this section',
    description: 'message displayed when an error occurs deleting a section',
  },
});

const useTaskListSectionDelete = (taskListId) => {
  const { formatMessage } = useIntl();
  const { template, deleteSection: deleteTemplateSection } =
    useContext(TemplateContext) || {};

  const [deleteTaskListSectionMutation] = useMutation(
    TASK_LIST_SECTION_DELETE,
    {
      update: (cache, { data }) => {
        const gqlErrors = getGqlErrors(data);

        if (gqlErrors.length) {
          toast.error(gqlErrors[0].message);
        }

        const deleteId = get(data, 'taskListSectionDelete.deletedId', '');

        if (deleteId) {
          const cacheId = defaultDataIdFromObject({
            id: taskListId,
            __typename: 'TaskList',
          });

          const cachedData = cache.readFragment({
            id: cacheId,
            fragment: TaskListSectionsFragment,
            fragmentName: 'TaskListSectionsFragment',
          });

          const taskListSections = get(
            cachedData,
            'taskListSections.nodes',
            [],
          );

          const updatedSectionTasksData = immutable.set(
            cachedData,
            `taskListSections.nodes`,
            taskListSections.filter((section) => section.id !== deleteId),
          );

          cache.writeFragment({
            id: cacheId,
            fragment: TaskListSectionsFragment,
            fragmentName: 'TaskListSectionsFragment',
            data: updatedSectionTasksData,
          });
        }
      },
      refetchQueries: [
        {
          query: GET_TASK_LIST_HEADER_INFO,
          variables: { id: taskListId },
        },
      ],
      onError: () => {
        toast.error(
          formatMessage({
            id: 'Projects.taskList.taskListSection.sectionDeleteError',
          }),
        );
      },
    },
  );

  const deleteTaskListSection = (section) => {
    if (template) {
      deleteTemplateSection(section);
    } else {
      deleteTaskListSectionMutation({
        variables: {
          input: {
            id: section.id,
          },
        },
      });
    }
  };

  return {
    deleteTaskListSection,
  };
};

export default useTaskListSectionDelete;
